<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    :width="dialogWidth"
    @close="$emit('update:dialogVisible', false)"
    class="ai-logic-diagram-dialog dialog-with-radius"
    append-to-body
  >
    <!-- 弹窗主体内容 -->
    <div class="logic-diagram-body" id="logicDiagramContent">
      <!-- 顶部中心节点 -->
      <div class="center-node-wrapper">
        <div class="center-node">
          <span class="center-icon">★</span>
          <span class="center-text">{{ centerNode }}</span>
        </div>
      </div>

      <!-- 三个核心模块区域 -->
      <div class="modules-container">
        <div
          v-for="(branch, index) in branches"
          :key="index"
          :class="['module-card', getModuleClass(index)]"
        >
          <div class="module-header">
            <span class="module-number">{{ index + 1 }}</span>
            <span class="module-title">{{ branch.name }}</span>
          </div>
          <div class="module-content">
            <div
              v-for="(item, idx) in branch.children"
              :key="idx"
              class="module-item"
            >
              <span class="item-dot"></span>
              <span class="item-text">{{ item }}</span>
            </div>
          </div>
          <div class="module-relation">
            <span class="relation-arrow">→</span>
            <span class="relation-text">{{ branch.relation }}</span>
          </div>
        </div>
      </div>

      <!-- 学习路径 -->
      <div class="section flow-section">
        <div class="section-header">
          <i class="el-icon-time"></i>
          <span class="section-title">学习路径</span>
        </div>
        <div class="flow-path">
          <span
            v-for="(step, index) in flowPathSteps"
            :key="index"
            class="flow-item-wrapper"
          >
            <span
              v-if="index > 0"
              class="flow-arrow"
            >→</span>
            <span
              :class="['flow-step', { 'last-step': index === flowPathSteps.length - 1 }]"
            >
              {{ step }}
            </span>
          </span>
        </div>
      </div>

      <!-- 核心能力培养 -->
      <div class="section competency-section">
        <div class="section-header">
          <i class="el-icon-star-on"></i>
          <span class="section-title">核心能力培养</span>
        </div>
        <div class="competencies">
          <el-tag
            v-for="(comp, index) in coreCompetencies"
            :key="index"
            type="success"
            effect="plain"
            class="competency-tag"
          >
            {{ comp }}
          </el-tag>
        </div>
      </div>

      <!-- 课程核心价值 -->
      <div class="section value-section">
        <div class="section-header">
          <i class="el-icon-lightbulb"></i>
          <span class="section-title">课程核心价值</span>
        </div>
        <div class="summary">
          <p>{{ summary }}</p>
        </div>
      </div>
    </div>

    <!-- 底部按钮 -->
    <template #footer>
      <div class="dialog-footer">
        <el-button class="footer-btn btn-close" plain @click="$emit('update:dialogVisible', false)">
          <i class="el-icon-close"></i> 关闭
        </el-button>
        <el-button class="footer-btn btn-save" type="primary" @click="handleSaveOptimized">
          <i class="el-icon-check"></i> 保存优化
        </el-button>
        <el-button class="footer-btn btn-png" @click="downloadPNG">
          <i class="el-icon-picture"></i> PNG
        </el-button>
        <el-button class="footer-btn btn-word" @click="downloadWord">
          <i class="el-icon-document"></i> Word
        </el-button>
        <el-button class="footer-btn btn-regen" plain @click="regenerate">
          <i class="el-icon-refresh"></i> 重新生成
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import html2canvas from 'html2canvas';
import { exportToWord } from '@/utils/exportToWord';

export default {
  name: 'AiLogicDiagram',
  props: {
    dialogTitle: {
      type: String,
      default: '课程大纲逻辑图'
    },
    dialogVisible: {
      type: Boolean,
      required: true
    },
    dialogWidth: {
      type: String,
      default: '900px'
    },
    detailData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      centerNode: '',
      branches: [],
      flowPath: '',
      coreCompetencies: [],
      summary: ''
    };
  },
  computed: {
    flowPathSteps() {
      return (this.flowPath || '').split(' → ').filter(step => step.trim() !== '');
    }
  },
  watch: {
    detailData: {
      immediate: true,
      handler(newVal) {
        this.centerNode = newVal.centerNode || '';
        this.branches = newVal.branches || [];
        this.flowPath = newVal.flowPath || '';
        this.coreCompetencies = newVal.coreCompetencies || [];
        this.summary = newVal.summary || '';
      }
    }
  },
  methods: {
    getModuleClass(index) {
      const classes = ['blue-module', 'green-module', 'orange-module'];
      return classes[index % classes.length];
    },

    handleSaveOptimized() {
      this.$emit('save-optimized', this.detailData);
    },

    async downloadPNG() {
      try {
        const captureElement = document.getElementById('logicDiagramContent');
        if (!captureElement) throw new Error('未找到截图区域');

        // 临时移除可能干扰截图的样式
        const originalStyle = captureElement.style.cssText;
        captureElement.style.boxShadow = 'none';
        captureElement.style.border = 'none';
        captureElement.style.padding = '20px';
        await this.$nextTick();

        const canvas = await html2canvas(captureElement, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          width: captureElement.scrollWidth,
          height: captureElement.scrollHeight
        });

        // 恢复原始样式
        captureElement.style.cssText = originalStyle;

        const imgDataUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = imgDataUrl;
        link.download = '课程大纲逻辑图.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$message.success('PNG图片已成功下载！');
      } catch (error) {
        console.error('PNG导出失败:', error);
        this.$message.error('导出PNG失败：' + (error.message || '未知错误'));
      }
    },

    async downloadWord() {
      try {
        const content = document.getElementById('logicDiagramContent');
        if (!content) throw new Error('未找到内容区域');

        // 构建带有完整样式的HTML
        const htmlContent = this.buildWordContent(content);
        await exportToWord(htmlContent, '课程大纲逻辑图.docx');
        this.$message.success('Word文档已成功下载！');
      } catch (error) {
        console.error('Word导出失败:', error);
        this.$message.error('导出Word失败：' + (error.message || '未知错误'));
      }
    },

    // 构建Word导出内容（保留样式）
    buildWordContent() {
      // 获取数据用于构建结构化的Word内容
      let html = `
        <div style="padding: 20px; font-family: 'Microsoft YaHei', sans-serif;">
          <!-- 中心节点 -->
          <div style="text-align: center; margin-bottom: 30px;">
            <div style="display: inline-block; background: linear-gradient(90deg, #6f42c1, #7d5fff); color: white; font-weight: bold; padding: 12px 40px; border-radius: 12px; font-size: 16px;">
              ★ ${this.centerNode}
            </div>
          </div>

          <!-- 模块区域 -->
          <div style="display: flex; justify-content: space-between; gap: 20px; margin-bottom: 30px;">
      `;

      // 三个模块
      const moduleColors = [
        { bg: '#e3f2fd', border: '#2196f3', header: '#1976d2' },
        { bg: '#e8f5e9', border: '#4caf50', header: '#388e3c' },
        { bg: '#fff3e0', border: '#ff9800', header: '#f57c00' }
      ];

      this.branches.forEach((branch, index) => {
        const colors = moduleColors[index % 3];
        html += `
          <div style="flex: 1; background: ${colors.bg}; border-left: 4px solid ${colors.border}; border-radius: 12px; padding: 20px; box-shadow: 0 4px 12px rgba(0,0,0,0.05);">
            <div style="font-size: 18px; font-weight: bold; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 2px solid rgba(0,0,0,0.1); color: ${colors.header};">
              ${index + 1}. ${branch.name}
            </div>
            <div style="margin-bottom: 15px;">
        `;
        (branch.children || []).forEach(item => {
          html += `
            <div style="background: rgba(255,255,255,0.7); padding: 10px 15px; margin: 8px 0; border-radius: 8px; border: 1px solid rgba(0,0,0,0.1); word-break: break-word;">
              • ${item}
            </div>
          `;
        });
        html += `
            </div>
            <div style="font-size: 14px; color: #666; line-height: 1.5; margin-top: 10px; padding-top: 10px; border-top: 1px dashed rgba(0,0,0,0.2);">
              → ${branch.relation}
            </div>
          </div>
        `;
      });

      html += `</div>`;

      // 学习路径
      html += `
        <div style="margin: 30px 0; padding: 20px; background: #fafafa; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.05);">
          <div style="margin-bottom: 15px; color: #333; font-weight: bold; display: flex; align-items: center;">
            <span style="margin-right: 8px;">📍</span> 学习路径
          </div>
          <div style="display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 10px;">
      `;
      this.flowPathSteps.forEach((step, index) => {
        const bg = index === this.flowPathSteps.length - 1 ? '#67c23a' : '#409eff';
        html += `
          <span style="display: inline-block; padding: 8px 16px; background: ${bg}; color: white; border-radius: 20px; font-weight: bold; box-shadow: 0 2px 6px rgba(64,158,255,0.3);">
            ${step}
          </span>
        `;
        if (index < this.flowPathSteps.length - 1) {
          html += `<span style="color: #999; font-weight: bold;">→</span>`;
        }
      });
      html += `</div></div>`;

      // 核心能力
      html += `
        <div style="margin: 30px 0; padding: 20px; background: #fafafa; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.05);">
          <div style="margin-bottom: 15px; color: #333; font-weight: bold; display: flex; align-items: center;">
            <span style="margin-right: 8px;">⭐</span> 核心能力培养
          </div>
          <div style="display: flex; flex-wrap: wrap; gap: 10px; justify-content: center;">
      `;
      this.coreCompetencies.forEach(comp => {
        html += `
          <span style="padding: 8px 16px; background: #e8f5e9; border: 1px solid #4caf50; border-radius: 20px; color: #388e3c; font-size: 14px;">
            ${comp}
          </span>
        `;
      });
      html += `</div></div>`;

      // 核心价值
      html += `
        <div style="margin: 30px 0; padding: 20px; background: #fffbe6; border-radius: 12px; border-left: 4px solid #faad14;">
          <div style="margin-bottom: 15px; color: #333; font-weight: bold; display: flex; align-items: center;">
            <span style="margin-right: 8px;">💡</span> 课程核心价值
          </div>
          <div style="line-height: 1.6; color: #333;">
            <p>${this.summary}</p>
          </div>
        </div>
      `;

      html += `</div>`;
      return html;
    },

    regenerate() {
      this.$emit('regenerate');
    }
  }
};
</script>

<style scoped>
/* 弹框基础样式 - 参考ajFormDetail */
::v-deep .el-dialog__title {
  font-weight: 800 !important;
}

::v-deep .el-dialog__header {
  background-color: #EFF5FF !important;
}

::v-deep .el-dialog {
  border-radius: 20px !important;
  overflow: hidden !important;
}

::v-deep .el-dialog__header {
  padding: 16px 20px;
  border-bottom: 1px solid #f5f5f5;
}

::v-deep .el-dialog__body {
  padding: 0 !important;
}

::v-deep .el-dialog__footer {
  padding: 16px 24px;
  border-top: 1px solid #f5f5f5;
  text-align: center;
  background-color: #F9FAFB;
}

/* 内容区域 */
.logic-diagram-body {
  padding: 20px 30px;
  max-height: 70vh;
  overflow-y: auto;
}

/* 中心节点 */
.center-node-wrapper {
  text-align: center;
  margin-bottom: 30px;
}

.center-node {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, #6f42c1 0%, #7d5fff 100%);
  color: white;
  font-weight: bold;
  padding: 14px 32px;
  border-radius: 50px;
  box-shadow: 0 6px 20px rgba(111, 66, 193, 0.35);
  font-size: 16px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.center-node:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(111, 66, 193, 0.4);
}

.center-icon {
  font-size: 20px;
}

/* 模块容器 */
.modules-container {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.module-card {
  flex: 1 1 30%;
  min-width: 250px;
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.module-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.module-card.blue-module {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-left: 4px solid #2196f3;
}

.module-card.green-module {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-left: 4px solid #4caf50;
}

.module-card.orange-module {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border-left: 4px solid #ff9800;
}

.module-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 17px;
  font-weight: bold;
  margin-bottom: 15px;
  padding-bottom: 12px;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
}

.module-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: white;
  font-size: 14px;
  font-weight: bold;
}

.blue-module .module-number {
  background: linear-gradient(135deg, #1976d2, #2196f3);
}

.green-module .module-number {
  background: linear-gradient(135deg, #388e3c, #4caf50);
}

.orange-module .module-number {
  background: linear-gradient(135deg, #f57c00, #ff9800);
}

.module-title {
  color: #333;
}

.module-content {
  margin-bottom: 15px;
}

.module-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(255, 255, 255, 0.8);
  padding: 12px 15px;
  margin: 10px 0;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  word-break: break-word;
  transition: all 0.2s ease;
}

.module-item:hover {
  background: rgba(255, 255, 255, 1);
  transform: translateX(4px);
}

.item-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  margin-top: 7px;
  opacity: 0.6;
}

.blue-module .item-dot { background: #2196f3; }
.green-module .item-dot { background: #4caf50; }
.orange-module .item-dot { background: #ff9800; }

.item-text {
  flex: 1;
  line-height: 1.5;
  color: #555;
  font-size: 14px;
}

.module-relation {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed rgba(0, 0, 0, 0.15);
}

.relation-arrow {
  font-weight: bold;
  color: #999;
}

.relation-text {
  color: #666;
}

/* 区块样式 */
.section {
  margin: 25px 0;
  padding: 20px 25px;
  background: #fafafa;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
  color: #333;
  font-weight: bold;
  font-size: 15px;
}

.section-header i {
  font-size: 18px;
  color: #409eff;
}

/* 学习路径 */
.flow-section .section-header i {
  color: #409eff;
}

.flow-path {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
}

.flow-arrow {
  color: #bbb;
  font-weight: bold;
  font-size: 16px;
  margin: 0 2px;
}

.flow-item-wrapper {
  display: inline-flex;
  align-items: center;
}

.flow-step {
  display: inline-block;
  padding: 10px 20px;
  background: linear-gradient(135deg, #409eff, #66b1ff);
  color: white;
  border-radius: 25px;
  font-weight: 600;
  font-size: 14px;
  box-shadow: 0 3px 10px rgba(64, 158, 255, 0.35);
  transition: transform 0.2s ease;
}

.flow-step:hover {
  transform: scale(1.05);
}

.flow-step.last-step {
  background: linear-gradient(135deg, #67c23a, #85ce61);
  box-shadow: 0 3px 10px rgba(103, 194, 58, 0.35);
}

/* 核心能力 */
.competency-section .section-header i {
  color: #67c23a;
}

.competencies {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
}

.competency-tag {
  margin: 0;
  font-size: 14px;
  padding: 8px 18px;
  border-radius: 20px;
  border-width: 2px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.competency-tag:hover {
  transform: scale(1.05);
}

/* 课程核心价值 */
.value-section {
  background: linear-gradient(135deg, #fffbe6 0%, #fff7d6 100%);
  border-left: 4px solid #faad14;
}

.value-section .section-header i {
  color: #faad14;
}

.summary {
  padding: 15px 20px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 8px;
  line-height: 1.8;
  color: #555;
  font-size: 14px;
}

.summary p {
  margin: 0;
}

/* 底部按钮 */
.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.footer-btn {
  min-width: 110px;
  height: 42px;
  border-radius: 12px !important;
  font-weight: 600 !important;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.3s ease !important;
}

.footer-btn i {
  font-size: 14px;
}

/* 关闭按钮 */
.btn-close {
  background: #ffffff !important;
  border-color: #dcdfe6 !important;
  color: #606266 !important;
}

.btn-close:hover {
  border-color: #c0c4cc !important;
  color: #909399 !important;
  background: #f5f7fa !important;
}

/* 保存按钮 */
.btn-save {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
  border-color: #8b5cf6 !important;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.35) !important;
}

.btn-save:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(139, 92, 246, 0.45) !important;
  background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
}

/* PNG按钮 */
.btn-png {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%) !important;
  border-color: #409eff !important;
  color: white !important;
}

.btn-png:hover {
  transform: translateY(-2px);
  background: linear-gradient(135deg, #66b1ff 0%, #409eff 100%) !important;
}

/* Word按钮 */
.btn-word {
  background: linear-gradient(135deg, #67c23a 0%, #85ce61 100%) !important;
  border-color: #67c23a !important;
  color: white !important;
}

.btn-word:hover {
  transform: translateY(-2px);
  background: linear-gradient(135deg, #85ce61 0%, #67c23a 100%) !important;
}

/* 重新生成按钮 */
.btn-regen {
  background: #ffffff !important;
  border-color: #409eff !important;
  color: #409eff !important;
}

.btn-regen:hover {
  background: #ecf5ff !important;
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .modules-container {
    flex-direction: column;
  }

  .module-card {
    min-width: 100%;
  }

  .flow-path {
    flex-direction: column;
    align-items: stretch;
  }

  .flow-step {
    width: 100%;
    text-align: center;
  }

  .footer-btn {
    width: calc(50% - 10px);
    min-width: auto;
  }
}
</style>
